/**
 * Hours per project, the basic version (backend/controllers/admin/projectsController.js, owner
 * 2026-09-29). Only asked for where the company's projects module is on and available.
 */
import { api, get, post, put } from '@/api/client';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface Project {
  id: number;
  number: string | null;
  name: string;
  /** the customer's name, from the customers table since #115 (the typed text before that) */
  customer: string | null;
  customer_id?: number | null;
  active: boolean;
}
/** A customer of the company (#115): the party the work is done for. */
export interface Customer {
  id: number;
  name: string;
  org_number: string | null;
  contact: string | null;
  active: boolean;
  /** how many projects point at it */
  projects?: number;
}
export interface ProjectGroup {
  project_id: number | null;
  number: string | null;
  name: string | null;
  customer_id?: number | null;
  customer: string | null;
  hours: number;
  /** every clocked hour in the period, approved and waiting, whatever the filter (decision 10) */
  approved?: number;
  waiting?: number;
  days: number;
  people: { user_id: number; name: string; hours: number; days: number }[];
}
export interface CustomerGroup {
  customer_id: number | null;
  name: string | null;
  hours: number;
  approved?: number;
  waiting?: number;
  days: number;
  projects: {
    project_id: number | null;
    number: string | null;
    name: string | null;
    hours: number;
    days: number;
  }[];
}
export interface ProjectReport {
  start_date: string;
  end_date: string;
  total: number;
  projects: ProjectGroup[];
  /** «Timer per kunde» (#115); absent from older servers */
  customers?: CustomerGroup[];
}
/** The rows the customer report (#123) prints, from GET /admin/projects/:id/hours. */
export interface ProjectHours {
  project: Project & { customer_org_number?: string | null };
  start_date: string;
  end_date: string;
  rows: {
    day: string;
    user_id: number;
    person: string;
    hours: number;
    approved: boolean;
    approved_by: string | null;
  }[];
  total: number;
}

/** Whether this company uses projects: enabled here and shipped in this release. */
export function useProjectsOn(): boolean {
  const { workspace } = useTenant();
  return !!(workspace.available.projects && workspace.enabled.projects);
}

/** The words a company may use for a project (Innstillinger → Prosjekter, #115), singular and plural. */
export const PROJECT_WORDS: Record<string, [string, string]> = {
  Prosjekt: ['Prosjekt', 'Prosjekter'],
  Oppdrag: ['Oppdrag', 'Oppdrag'],
  Objekt: ['Objekt', 'Objekter'],
};
/**
 * The company's word for a project, as the UI says it everywhere: `one` is «Prosjekt», «Oppdrag»
 * or «Objekt», `many` the plural, `none` «Uten prosjekt». Translated through T(), so English reads
 * Project, Job or Object. The data model keeps `project` whatever the word.
 */
export function useProjectWord() {
  const { tenant } = useTenant();
  const key = (tenant as { projects_label?: string | null } | null)?.projects_label ?? 'Prosjekt';
  const [one, many] = PROJECT_WORDS[key] ?? (['Prosjekt', 'Prosjekter'] as [string, string]);
  return {
    key,
    one: T(one),
    many: T(many),
    none: T(`Uten ${one.toLowerCase()}`),
    lower: T(one).toLowerCase(),
  };
}
/** Whether a shift here should carry a project (Innstillinger → Prosjekter, «Prosjekt påkrevd»). */
export function useProjectRequired(): boolean {
  const { tenant } = useTenant();
  return useProjectsOn() && !!(tenant as { projects_required?: boolean } | null)?.projects_required;
}

const KEY = (t?: number) => ['projects', t];
export function useProjects() {
  const { tenant } = useTenant();
  const on = useProjectsOn();
  return useQuery({
    queryKey: KEY(tenant?.id),
    queryFn: async () => (await get<{ projects: Project[] }>('/admin/projects')).projects,
    enabled: on,
  });
}

const CKEY = (t?: number) => ['customers', t];
/** The company's customers, for the project drawer and the report; asked for by a reader of hours only. */
export function useCustomers() {
  const { tenant } = useTenant();
  const on = useProjectsOn();
  const may = useCan()('timeliste.se');
  return useQuery({
    queryKey: CKEY(tenant?.id),
    queryFn: async () => (await get<{ customers: Customer[] }>('/admin/customers')).customers,
    enabled: on && may,
  });
}
export function useSaveCustomer() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  return useMutation({
    mutationFn: (c: Partial<Customer> & { id?: number }) =>
      c.id ? put<Customer>(`/admin/customers/${c.id}`, c) : post<Customer>('/admin/customers', c),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: CKEY(tenant?.id) }),
        qc.invalidateQueries({ queryKey: KEY(tenant?.id) }),
      ]),
  });
}

export function useSaveProject() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  return useMutation({
    mutationFn: (p: Partial<Project> & { id?: number }) =>
      p.id ? put<Project>(`/admin/projects/${p.id}`, p) : post<Project>('/admin/projects', p),
    // a typed customer name may have made a customer row too
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: KEY(tenant?.id) }),
        qc.invalidateQueries({ queryKey: CKEY(tenant?.id) }),
      ]),
  });
}

export function useProjectReport(from: string, to: string, approvedOnly: boolean) {
  const { tenant } = useTenant();
  const on = useProjectsOn();
  // the server answers the report for timesheet.view only; nobody else asks to be refused
  const may = useCan()('timeliste.se');
  return useQuery({
    queryKey: ['project-report', tenant?.id, from, to, approvedOnly],
    queryFn: () =>
      get<ProjectReport>(`/admin/projects/report?start_date=${from}&end_date=${to}&approved=${approvedOnly}`),
    enabled: on && may,
  });
}

/** The CSV for the accountant: one line per person, project and day. */
export const projectCsv = (from: string, to: string, approvedOnly: boolean) =>
  get<string>(`/admin/projects/report.csv?start_date=${from}&end_date=${to}&approved=${approvedOnly}`);

/** «Timer per kunde» for the accountant: one line per customer, project, person and day. */
export const customersCsv = (from: string, to: string, approvedOnly: boolean) =>
  get<string>(`/admin/projects/customers.csv?start_date=${from}&end_date=${to}&approved=${approvedOnly}`);

/** The rows behind one project's customer report (#123). */
export const projectHours = (id: number, from: string, to: string, approvedOnly: boolean) =>
  get<ProjectHours>(`/admin/projects/${id}/hours?start_date=${from}&end_date=${to}&approved=${approvedOnly}`);

/** A shift's project, set or cleared by the manager afterwards. */
export const setShiftProject = (shiftId: number, projectId: number | null) =>
  put<{ id: number; project_id: number | null }>(`/admin/shifts/${shiftId}/project`, {
    project_id: projectId,
  });

/** How a project is named on a screen: its number first when it has one. */
export const projectLabel = (p: { number: string | null; name: string | null } | null | undefined) =>
  !p || !p.name ? 'Uten prosjekt' : p.number ? `${p.number} ${p.name}` : p.name;

/* ── the budget from the firm's own estimate (#132, controllers/admin/projectBudgetController.js) ── */

/** One budget line: a post of the estimate, its work type (the shift type), hours and kroner for materials. */
export interface BudgetLine {
  id?: number;
  label: string;
  shift_type_id: number | null;
  work_type?: string | null;
  hours: number | null;
  materials: number | null;
}
/** A project's budget against its clocked hours, approved and waiting, since the start. */
export interface ProjectBudget {
  project_id: number;
  number: string | null;
  name: string;
  customer: string | null;
  active: boolean;
  budget: number;
  used: number;
  approved: number;
  waiting: number;
  left: number;
  materials: number | null;
  percent: number | null;
  warn_percent: number;
  warn: boolean;
  /** how far along the manager said the job is: 25, 50, 75, 100 */
  progress: number | null;
  progress_at: string | null;
  /** hours at completion from the burn rate, and that less the budget */
  forecast: number | null;
  overrun: number | null;
  by_type: { shift_type_id: number | null; work_type: string | null; budget: number; used: number }[];
  lines?: BudgetLine[];
}
/** An estimate read into lines, not saved: by code from CSV or Excel, or by Hugin's model. */
export interface EstimateRead {
  source: 'csv' | 'xlsx' | 'model';
  note: string | null;
  lines: BudgetLine[];
}

const BKEY = (t?: number) => ['project-budgets', t];
/** Every project with a budget or a progress; for a reader of hours only, as the server answers. */
export function useBudgets() {
  const { tenant } = useTenant();
  const on = useProjectsOn();
  const may = useCan()('timeliste.se');
  return useQuery({
    queryKey: BKEY(tenant?.id),
    queryFn: () => get<{ warn_percent: number; projects: ProjectBudget[] }>('/admin/projects/budgets'),
    enabled: on && may,
  });
}
/** One project's budget with its lines. */
export function useProjectBudget(id: number | null) {
  const { tenant } = useTenant();
  const on = useProjectsOn();
  const may = useCan()('timeliste.se');
  return useQuery({
    queryKey: [...BKEY(tenant?.id), id],
    queryFn: () => get<ProjectBudget>(`/admin/projects/${id}/budget`),
    enabled: on && may && id != null,
  });
}
/** The confirmed lines replace the project's lines (the administrator's). */
export function useSaveBudget() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  return useMutation({
    mutationFn: ({ id, lines }: { id: number; lines: BudgetLine[] }) =>
      put<ProjectBudget>(`/admin/projects/${id}/budget`, { lines }),
    onSuccess: () => qc.invalidateQueries({ queryKey: BKEY(tenant?.id) }),
  });
}
/** The progress tap: 25, 50, 75, 100, or null to clear it. */
export function useSetProgress() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  return useMutation({
    mutationFn: ({ id, progress }: { id: number; progress: number | null }) =>
      put<ProjectBudget>(`/admin/projects/${id}/progress`, { progress }),
    onSuccess: () => qc.invalidateQueries({ queryKey: BKEY(tenant?.id) }),
  });
}
/** An estimate file read into lines; nothing is saved until the manager confirms them. */
export function readEstimate(id: number, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return api<EstimateRead>(`/admin/projects/${id}/estimate`, { method: 'POST', body: fd });
}
